import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PageHeader,
  SectionCard,
  FieldLabel,
  StatusBadge,
  ApproverRoleBadge,
  WarningBanner,
  EmptyState,
  useConfirm,
} from "./shared/components";
import {
  getBondTypes,
  saveBondType,
  deleteBondType,
  BondType,
  BondTypeField,
  BondFieldType,
} from "./services/eSeparation.service";

const emptyType: Partial<BondType> = {
  id: undefined,
  code: "",
  name: "",
  description: "",
  is_active: true,
  sort_order: 0,
  fields: [],
};

const FIELD_TYPE_OPTIONS: { value: BondFieldType; label: string }[] = [
  { value: "TEXT", label: "Text" },
  { value: "NUMBER", label: "Number" },
  { value: "DATE", label: "Date" },
  { value: "BOOLEAN", label: "Yes / No" },
  { value: "TEXTAREA", label: "Long text" },
];

// Repeatable-row idiom for the dynamic field editor — mirrors
// NoDuesDeptAction.tsx's blankRow/addRow/updateRow/removeFromForm pattern.
interface FieldRow extends BondTypeField {
  key: string;
}

let rowSeq = 0;
const newKey = () => `bondfield_${Date.now()}_${rowSeq++}`;
const blankFieldRow = (): FieldRow => ({
  key: newKey(),
  field_key: "",
  field_label: "",
  field_type: "TEXT",
  is_required: false,
  sort_order: 0,
});

const BondTypeConfig: React.FC = () => {
  const navigate = useNavigate();
  const { confirm, confirmDialog } = useConfirm();

  const [types, setTypes] = useState<BondType[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Partial<BondType>>({ ...emptyType });
  const [fieldRows, setFieldRows] = useState<FieldRow[]>([blankFieldRow()]);
  const [saving, setSaving] = useState(false);

  const loadTypes = async () => {
    setLoading(true);
    const res = await getBondTypes(false);
    setTypes(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const editType = (t: BondType) => {
    setForm({ ...t });
    setFieldRows(
      t.fields.length
        ? t.fields.map((f) => ({ ...f, key: newKey() }))
        : [blankFieldRow()]
    );
  };

  const resetForm = () => {
    setForm({ ...emptyType });
    setFieldRows([blankFieldRow()]);
  };

  const updateFieldRow = (key: string, patch: Partial<FieldRow>) =>
    setFieldRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addFieldRow = () => setFieldRows((prev) => [...prev, blankFieldRow()]);
  const removeFieldRow = (key: string) =>
    setFieldRows((prev) => prev.filter((r) => r.key !== key));

  const saveType = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      return toast.warning("Bond type code and name are required.");
    }
    const cleanFields = fieldRows.filter(
      (r) => r.field_key.trim() && r.field_label.trim()
    );
    setSaving(true);
    const res = await saveBondType({
      ...form,
      fields: cleanFields.map((r, i) => ({
        id: r.id,
        field_key: r.field_key.trim(),
        field_label: r.field_label.trim(),
        field_type: r.field_type,
        is_required: r.is_required,
        sort_order: i,
      })),
    });
    setSaving(false);
    if (res.success) {
      toast.success(form.id ? "Bond type updated." : "Bond type added.");
      resetForm();
      loadTypes();
    } else {
      toast.error(res.message || "Failed to save bond type.");
    }
  };

  const removeType = async (t: BondType) => {
    if (
      !(await confirm(`Delete bond type "${t.name}"?`, {
        title: "Delete Bond Type",
        confirmLabel: "Delete",
        tone: "danger",
      }))
    )
      return;
    const res = await deleteBondType(t.id);
    if (res.success) {
      toast.success("Bond type deleted.");
      if (form.id === t.id) resetForm();
      loadTypes();
    } else {
      toast.error(res.message || "Failed to delete bond type.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content">
        <PageHeader
          title="Bond Types"
          subtitle="Configure the bond types & their fields employees select during resignation"
          right={<ApproverRoleBadge role="HR" label="HR Admin" />}
          onExit={() => navigate(-1)}
        />

        <WarningBanner
          type="info"
          message="Bond types configured here appear as a multi-select on the resignation form. Each type's fields below are what the employee fills in when they select it."
        />

        <div className="row">
          {/* Left — list */}
          <div className="col-lg-7">
            <SectionCard index={0} title="Bond Types">
              {loading ? (
                <EmptyState icon="ti-loader" title="Loading..." />
              ) : types.length === 0 ? (
                <EmptyState
                  icon="ti-shield-off"
                  title="No bond types yet"
                  message="Add your first bond type."
                />
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Fields</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {types.map((t) => (
                        <tr key={t.id}>
                          <td className="fw-medium">{t.code}</td>
                          <td>{t.name}</td>
                          <td>{t.fields?.length ?? 0}</td>
                          <td>
                            <StatusBadge status={t.is_active ? "Active" : "Inactive"} />
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => editType(t)}
                            >
                              <i className="ti ti-edit" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeType(t)}
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>

          {/* Right — add/edit */}
          <div className="col-lg-5">
            <SectionCard index={1} title={form.id ? "Edit Bond Type" : "Add Bond Type"}>
              <div className="mb-3">
                <FieldLabel label="Bond Type Code" required />
                <input
                  className="form-control"
                  value={form.code || ""}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="e.g. TRAINING"
                />
              </div>
              <div className="mb-3">
                <FieldLabel label="Bond Type Name" required />
                <input
                  className="form-control"
                  value={form.name || ""}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Training Bond"
                />
              </div>
              <div className="mb-3">
                <FieldLabel label="Description" />
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.description || ""}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional note shown to HR only"
                />
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <FieldLabel label="Sort Order" />
                  <input
                    type="number"
                    className="form-control"
                    value={form.sort_order ?? 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="col-6 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="bondtype-active"
                      checked={form.is_active ?? true}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="bondtype-active">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="border-top pt-3 mt-2 mb-2">
                <FieldLabel label="Fields employees fill in for this bond type" />
              </div>

              <div className="d-flex flex-column gap-2 mb-2">
                {fieldRows.map((row) => (
                  <div key={row.key} className="border rounded p-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Field</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeFieldRow(row.key)}
                        disabled={fieldRows.length === 1}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                    <input
                      className="form-control form-control-sm mb-2"
                      placeholder="Field label (e.g. Bond Period)"
                      value={row.field_label}
                      onChange={(e) =>
                        updateFieldRow(row.key, { field_label: e.target.value })
                      }
                    />
                    <input
                      className="form-control form-control-sm mb-2"
                      placeholder="Field key (e.g. bond_period)"
                      value={row.field_key}
                      onChange={(e) =>
                        updateFieldRow(row.key, {
                          field_key: e.target.value.trim().toLowerCase().replace(/\s+/g, "_"),
                        })
                      }
                    />
                    <div className="d-flex gap-2 align-items-center">
                      <select
                        className="form-select form-select-sm"
                        value={row.field_type}
                        onChange={(e) =>
                          updateFieldRow(row.key, {
                            field_type: e.target.value as BondFieldType,
                          })
                        }
                      >
                        {FIELD_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <div className="form-check text-nowrap">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`req-${row.key}`}
                          checked={row.is_required ?? false}
                          onChange={(e) =>
                            updateFieldRow(row.key, { is_required: e.target.checked })
                          }
                        />
                        <label className="form-check-label small" htmlFor={`req-${row.key}`}>
                          Required
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-3"
                onClick={addFieldRow}
                style={{ borderStyle: "dashed" }}
              >
                <i className="ti ti-plus me-1" />
                Add Field
              </button>

              <div className="d-flex gap-2">
                <button className="btn btn-primary" onClick={saveType} disabled={saving}>
                  <i className="ti ti-device-floppy me-1" />
                  {form.id ? "Update" : "Add"}
                </button>
                {form.id && (
                  <button className="btn btn-outline-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BondTypeConfig;
